// Hooks para el módulo lead-magnets — sin react-query (no está en deps).
// Estado local + refetch manual; mismo pattern que expo-capture.

import { useCallback, useEffect, useState } from 'react';

import { leadMagnetsApi, LeadMagnetsApiError } from '../api';
import type {
  EmailSequence,
  EmailSequenceCreateIn,
  LeadMagnet,
  LeadMagnetCreateIn,
  LeadMagnetListFilters,
  LeadMagnetListResponse,
  SubscriberListResponse,
} from '../types';

function errMsg(e: unknown): string {
  if (e instanceof LeadMagnetsApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

export interface UseLeadMagnetsResult {
  data: LeadMagnetListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useLeadMagnets(filters: LeadMagnetListFilters = {}): UseLeadMagnetsResult {
  const [data, setData] = useState<LeadMagnetListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const filterKey = JSON.stringify(filters);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await leadMagnetsApi.list(filters));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export interface UseCreateLeadMagnetResult {
  submit: (data: LeadMagnetCreateIn) => Promise<LeadMagnet | null>;
  loading: boolean;
  error: string | null;
}

export function useCreateLeadMagnet(): UseCreateLeadMagnetResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useCallback(async (data: LeadMagnetCreateIn) => {
    setLoading(true);
    setError(null);
    try {
      return await leadMagnetsApi.create(data);
    } catch (e) {
      setError(errMsg(e));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);
  return { submit, loading, error };
}

export interface UseSubscribersResult {
  data: SubscriberListResponse | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useSubscribers(
  magnetId: number | null,
  opts: { limit?: number; offset?: number } = {},
): UseSubscribersResult {
  const [data, setData] = useState<SubscriberListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const optsKey = JSON.stringify(opts);

  const refetch = useCallback(async () => {
    if (magnetId == null) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      setData(await leadMagnetsApi.listSubscribers(magnetId, opts));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [magnetId, optsKey]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { data, loading, error, refetch };
}

export interface UseCreateSequenceResult {
  submit: (
    magnetId: number,
    data: EmailSequenceCreateIn,
  ) => Promise<EmailSequence | null>;
  loading: boolean;
  error: string | null;
}

export function useCreateSequence(): UseCreateSequenceResult {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = useCallback(
    async (magnetId: number, data: EmailSequenceCreateIn) => {
      setLoading(true);
      setError(null);
      try {
        return await leadMagnetsApi.createSequence(magnetId, data);
      } catch (e) {
        setError(errMsg(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );
  return { submit, loading, error };
}
